import { useState, useEffect, useContext } from "react";
import { api } from "../../services/api";
import Loading from "../../components/Loading";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { PaginatorContext } from "../../providers/Paginator";
import VolverButton from "../../components/VolverButton";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Container, Button, Table, TablePagination, Menu, ListItemIcon,
  ListItemText, TableBody, MenuItem, Paper, TableContainer,
  TableHead, TableRow, TableCell, CircularProgress, Alert,
  TextField, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, DialogContentText
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import {
  Search as SearchIcon, FileDownloadOutlined, MoreVert,
  Print, Block, Receipt
} from '@mui/icons-material';
import withAuthenticationRequired from "../../components/ProtectedRoutes/withAuthenticationRequired";
import ImprimirTickets from "../../components/modales/imprimirModal/index";
import useEventoStore from "../../stores/useEventoStore";
import { ROL } from "../../domain/staff/roles";

const Vouchers = () => {
  const history = useHistory();
  const param = useParams()
  const idRol = useEventoStore((state) => state.eventosPorId[param.id]?.data?.eventoUsuario?.idrol ?? null);
  const puedeDescargar = [ROL.OWNER, ROL.CLIENTE, ROL.COLABORADOR, ROL.ADMIN_DATOS].includes(idRol);
  const [t] = useTranslation("global");
  const [loading, setLoading] = useState();
  const [tarifasId, setTarifasId] = useState([]);
  const [filtroTarifas, setFiltroTarifas] = useState("Todo");
  const [data, setData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openMore, setOpenMore] = useState(null);
  const [openDisable, setOpenDisable] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [motivoBaja, setMotivoBaja] = useState("");
  const [loadingBaja, setLoadingBaja] = useState(false);

  const {
    query,
    setQuery,
    totalLength,
    setTotalLength,
    currentPage,
    setCurrentPage,
    setPageSize,
    pageSize
  } = useContext(PaginatorContext);

  const handleRowClick = (voucher) => {
    const url = `/eventos/${param.id}/vouchers/${voucher.id}`;
    window.open(url, "_blank");
  };

  const notify = (type, message, position = "bottom-center") => {
    const options = {
      position,
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    }
    switch (type) {
      case 'success':
        toast.success(message, options);
        break;
      case 'error':
        toast.error(message, options);
        break;
      case 'info':
        toast.info(message, options);
        break;
      case 'warning':
        toast.warning(message, options);
        break;
      default:
        toast(message, options);
    }
  }

  const openSeeMore = Boolean(anchorEl);

  const handleClickSeeMore = (e) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseSeeMore = (e) => {
    e.stopPropagation()
    setAnchorEl(null)
  }

  const fetchVouchers = async (id) => {
    setLoading(true);
    try {
      const response = await api.vouchers.getVouchers(id, query, pageSize, currentPage);

      if (response.status === "success") {
        const vouchers = response.data.vouchers || [];
        setData(vouchers);
        setTotalLength(response.data.totalDataSize || 0);

        const tarifasConIdsUnicas = [
          ...new Map(
            vouchers.map((voucher) => [
              voucher.tarifa.id,
              { id: voucher.tarifa.id, concepto: voucher.tarifa.concepto }
            ])
          ).values(),
        ];
        setTarifasId(tarifasConIdsUnicas);
      }
    } catch (error) {
      toast.error(error.message, { position: "top-center", autoClose: 4000 });
      setData([]);
      setTotalLength(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers(param.id);
    document.title = `Vouchers | PassGo`;
  }, [param.id, query, pageSize, currentPage, setTotalLength]);

  const HandleDownloadReport = (e) => {
    e.stopPropagation()

    const callExcel = async (e) => {
      try {
        const response = await api.reporte.downloadReporteVoucher(param.id)
        if (!response) {
          notify("error", "Error al realizar la descarga")
          throw new Error('Error al descargar el Excel')
        }
        const blob = await response;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ReporteVoucher-${param.id}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        notify("success", "La descarga se realizó con éxito")
      } catch (error) {
        notify("error", "Hubo un problema al descargar el archivo");
      }
    }
    callExcel()
  }

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleVerVenta = (voucher) => {
    setOpenMore(null);
    history.push(`/eventos/${param.id}/ventas/${voucher.idcompra}`);
  };

  const handleDisable = (voucher) => {
    setOpenMore(null);
    setOpenDisable(true);
    setSelectedVoucher(voucher);
    setMotivoBaja("");
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery(searchValue);
    setCurrentPage(0);
  };

  const handleDisableVoucher = async () => {
    if (!motivoBaja.trim()) {
      notify("warning", "Debe ingresar un motivo para la baja");
      return;
    }

    setLoadingBaja(true);
    try {
      const response = await api.vouchers.darDeBajaVoucher(selectedVoucher.id, motivoBaja);

      if (response.status === "success") {
        notify("success", "Voucher dado de baja correctamente");
        setOpenDisable(false);
        setSelectedVoucher(null);
        setMotivoBaja("");
        fetchVouchers(param.id);
      } else {
        notify("error", response.message || "Error al dar de baja el voucher");
      }
    } catch (error) {
      notify("error", error.message || "Error al dar de baja el voucher");
    } finally {
      setLoadingBaja(false);
    }
  };

  const handleCloseDisableModal = () => {
    setOpenDisable(false);
    setSelectedVoucher(null);
    setMotivoBaja("");
  };

  return (
    <Container>
      <div className="d-flex justify-content-between flex-wrap">
        <div className="d-flex flex-row gap-2">
          <form onSubmit={handleSearch}>
            <div className="w-100 d-flex" >
              <TextField
                id="exampleSearch"
                name="q"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Buscar por ID, código, nombre o apellido..."
                type="search"
                variant="outlined"
                size="small"
                sx={{ height: "100%", flex: 1 }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      type="submit"
                      size="small"
                      sx={{ marginRight: "-8px" }}
                    >
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </div>
          </form>
        </div>
        <div className="d-flex flex-row gap-3 align-items-center">
          <Button variant="contained" color="success" startIcon={<AddIcon />}
            onClick={() => history.push("/eventos/" + param.id + "/nuevoVoucher")}
          >
            Nuevo voucher
          </Button>

          <IconButton
            aria-label="see more"
            id={param.id}
            aria-controls={openSeeMore ? 'long-menu' : undefined}
            aria-expanded={openSeeMore ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClickSeeMore}
            sx={{ p: 0 }}
            className="ms-auto"
          >
            <MoreVert />
          </IconButton>
          <Menu
            id={param.id}
            aria-label="report"
            anchorEl={anchorEl}
            open={param.id === anchorEl?.id}
            onClose={handleCloseSeeMore}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {puedeDescargar && (
            <MenuItem onClick={HandleDownloadReport}>
              <ListItemIcon>
                <FileDownloadOutlined sx={{ color: "#5656FF" }} />
              </ListItemIcon>
              <ListItemText>Descargar vouchers</ListItemText>
            </MenuItem>
            )}
            <MenuItem onClick={handleOpenModal}>
              <ListItemIcon>
                <Print sx={{ color: "#5656FF" }} />
              </ListItemIcon>
              <ListItemText>Imprimir vouchers</ListItemText>
            </MenuItem>
          </Menu>
        </div>
      </div>

      <TableContainer component={Paper} className="mt-3 table-responsive">
        <Table aria-label="simple table">
          <TableHead sx={{ backgroundColor: "#E6E6FF" }}>
            <TableRow sx={{ borderColor: "#E6E6FF", textAlign: "center" }} >
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>#</TableCell>
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>{t("tickets.code")}</TableCell>
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Vouchers</TableCell>
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Cantidad</TableCell>
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>{t("tickets.name")}</TableCell>
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>{t("tickets.lastName")}</TableCell>
              <TableCell sx={{ fontSize: "14px", fontWeight: "600", color: "#0B0060", fontFamily: "Bricolage Grotesque", }}>Acción</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              data?.length > 0 ? (
                data?.map((voucher) => (
                  <TableRow key={voucher.id}
                    hover
                    onDoubleClick={() => handleRowClick(voucher)}>
                    <TableCell component="th" scope="row">
                      {voucher.id}
                    </TableCell>

                    <TableCell>{voucher.codigo}</TableCell>
                    <TableCell>{voucher.tarifa.concepto}</TableCell>
                    <TableCell align="center">{voucher.cantidad}</TableCell>
                    <TableCell>{voucher.nombre}</TableCell>
                    <TableCell>{voucher.apellido}</TableCell>

                    <TableCell>
                      <div style={{ display: "flex", justifyContent: voucher.consumido === 1 ? "space-between" : "end", alignItems: "center" }}>
                        {(voucher.consumido === 1) ? (
                          <Chip
                            size="small"
                            label="Consumido"
                            icon={<Block />}
                            color="error"
                            variant="outlined"
                          />
                        ) : null}

                        <IconButton onClick={(e) => setOpenMore(e.currentTarget)} id={voucher.id}>
                          <MoreVert fontSize="inherit" />
                        </IconButton>
                        <Menu
                          id="basic-menu"
                          anchorEl={openMore}
                          open={voucher.id === parseInt(openMore?.id)}
                          onClose={() => { setOpenMore(null); setSelectedVoucher(null) }}
                          MenuListProps={{
                            'aria-labelledby': voucher.id,
                          }}
                        >
                          <MenuItem onClick={() => handleVerVenta(voucher)}>
                            <ListItemIcon>
                              <Receipt fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Ver Venta</ListItemText>
                          </MenuItem>
                          <MenuItem onClick={() => handleDisable(voucher)} disabled={voucher.consumido === 1}>
                            <ListItemIcon>
                              <Block fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Deshabilitar</ListItemText>
                          </MenuItem>
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Alert severity="info">
                      No hay vouchers aún.
                    </Alert>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          className="d-flex justify-content-center"
          rowsPerPageOptions={[10, 25, 50]}
          count={totalLength || 0}
          rowsPerPage={pageSize}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Registros p/página"
          showFirstButton
          showLastButton
        />
      </TableContainer>

      <Dialog
        open={openDisable}
        onClose={handleCloseDisableModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px"
          },
        }}
      >
        <DialogTitle sx={{
          fontWeight: 600,
          fontSize: "20px",
          color: "#d32f2f",
          pb: 1
        }}>
          ¿Desea deshabilitar este voucher?
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: "#666" }}>
            Una vez realizado, el dueño del voucher no podrá canjearlo. Esta acción no se puede revertir.
          </DialogContentText>

          {selectedVoucher && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Voucher:</strong> {selectedVoucher.tarifa?.concepto}<br />
              <strong>Titular:</strong> {selectedVoucher.nombre} {selectedVoucher.apellido}<br />
              <strong>Código:</strong> {selectedVoucher.codigo}<br />
              <strong>Cantidad:</strong> {selectedVoucher.cantidad}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Motivo de la baja"
            placeholder="Ej: Voucher duplicado, fraude, etc."
            value={motivoBaja}
            onChange={(e) => setMotivoBaja(e.target.value)}
            multiline
            rows={3}
            required
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#5656ff',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#5656ff',
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{
          gap: "8px",
          px: 3,
          pb: 2,
          justifyContent: "space-between"
        }}>
          <Button
            onClick={handleCloseDisableModal}
            disabled={loadingBaja}
            sx={{
              color: "#666",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#f5f5f5"
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDisableVoucher}
            variant="contained"
            color="error"
            startIcon={loadingBaja ? <CircularProgress size={20} color="inherit" /> : <Block />}
            disabled={loadingBaja || !motivoBaja.trim()}
            sx={{
              textTransform: "none",
              fontWeight: 600
            }}
          >
            {loadingBaja ? "Deshabilitando..." : "Sí, deshabilitar voucher"}
          </Button>
        </DialogActions>
      </Dialog>

      {openModal && (
        <ImprimirTickets
          onClose={handleCloseModal}
          filtroTarifas={filtroTarifas}
          tarifasId={tarifasId}
          idevento={param.id}
          tipo="vouchers"
        />
      )}

      <div className="d-flex justify-content-end mt-3">
        <VolverButton />
      </div>
    </Container>
  )
};

export default withAuthenticationRequired(Vouchers, {
  onRedirecting: () => <Loading />,
});